/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState, useEffect } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { groupBy } from 'lodash'

import type { EuiSelectableOption } from '@elastic/eui'
import {
  EuiFlexItem,
  EuiFlexGroup,
  EuiButtonEmpty,
  EuiSkeletonText,
  EuiSpacer,
  EuiSelectable,
  EuiPopover,
  EuiTextColor,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SaasAuthMfaDeviceResponse } from '@modules/cloud-api/v1/types'
import { CuiAlert } from '@modules/cui/Alert'
import { useGetSaasUserOktaGroups } from '@modules/okta-lib'
import { useGetSaasUserMfaDevices, useResetSaasUserMfa } from '@modules/mfa-lib'

import DangerButton from '../../DangerButton'

type Props = {
  userId: string
  email: string
}

const messages = defineMessages({
  label: {
    id: `reset-mfa-button.label`,
    defaultMessage: `Multifactor authentication`,
  },
  resetMfa: {
    id: `reset-mfa-button.reset-mfa`,
    defaultMessage: `Reset MFA`,
  },
  confirmationTitle: {
    id: `reset-mfa-button.are-you-sure`,
    defaultMessage: `Reset MFA?`,
  },
  confirmationBody: {
    id: `reset-mfa-button.confirmation-body`,
    defaultMessage: `You're about to reset MFA settings of {deviceName} for the account that's associated with {email}. This action cannot be undone.`,
  },
  confirmAction: {
    id: `reset-mfa-button.confirm-action`,
    defaultMessage: `Yes, reset MFA`,
  },
  mfaNotEnabled: {
    id: `reset-mfa-button.disabled-mfa`,
    defaultMessage: `Not enabled`,
  },
  mfaEnabled: {
    id: `reset-mfa-button.enabled-mfa`,
    defaultMessage: `{devices} {devices, plural, one {device} other {devices}}`,
  },
})
type DeviceSelectableOption = EuiSelectableOption<
  | SaasAuthMfaDeviceResponse
  | {
      label: string
      isGroupLabel?: boolean
      checked?: string
    }
>

function getOptions(activeDevices: SaasAuthMfaDeviceResponse[]) {
  const groupedDevices = groupBy(activeDevices, ({ device_type }) => device_type)
  const initialOptions: DeviceSelectableOption[] = []

  initialOptions.push({
    label: 'All MFA Devices',
    checked: 'on',
  })
  Object.entries(groupedDevices).forEach(([key, value]) => {
    const mappedOptions: DeviceSelectableOption[] = value.map((activeDevice) => ({
      ...activeDevice,
      label: activeDevice.device_id,
      checked: undefined,
    }))

    initialOptions.push({
      isGroupLabel: true,
      label: key,
    })
    initialOptions.push(...mappedOptions)
  })

  return initialOptions
}

const MfaManagement: React.FC<Props> = ({ userId, email }) => {
  const [options, setOptions] = useState<DeviceSelectableOption[]>([])
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { formatMessage } = useIntl()

  const { data: mfaDevicesResponse, isLoading } = useGetSaasUserMfaDevices(userId)

  const { data: oktaGroupsResponse, isLoading: oktaGroupsLoading } =
    useGetSaasUserOktaGroups(userId)

  const resetMfaMutation = useResetSaasUserMfa()

  useEffect(() => {
    if (mfaDevicesResponse?.mfa_devices) {
      setOptions(getOptions(mfaDevicesResponse.mfa_devices))
    }
  }, [mfaDevicesResponse])
  const actionText = formatMessage(messages.resetMfa)
  const selectedDevice = options.find((deviceOptions) => deviceOptions.checked === 'on')
  const selectedDeviceId =
    selectedDevice && 'device_id' in selectedDevice ? selectedDevice.device_id : undefined

  if (isLoading || oktaGroupsLoading) {
    return <EuiSkeletonText data-test-id='mfa-devices-loading' lines={1} />
  }

  const mfaDevices = mfaDevicesResponse?.mfa_devices || []
  const isInCloudMFAGroup = oktaGroupsResponse?.groups.find((group) => group.name === 'Cloud MFA')

  if (mfaDevices.length === 0 && !isInCloudMFAGroup) {
    return <FormattedMessage data-test-id='mfa-not-enabled' {...messages.mfaNotEnabled} />
  }

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='xs' alignItems='center' responsive={false}>
        <EuiFlexItem grow={false}>
          <FormattedMessage
            data-test-id='mfa-enabled'
            {...messages.mfaEnabled}
            values={{
              devices: mfaDevices.length,
            }}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <DangerButton
            buttonType={EuiButtonEmpty}
            buttonProps={{ size: 'xs' }}
            data-test-id='reset-mfa-button'
            onConfirm={() =>
              resetMfaMutation.mutate({
                userId,
                deviceId: selectedDeviceId,
              })
            }
            spin={resetMfaMutation.isLoading}
            modal={{
              title: formatMessage(messages.confirmationTitle),
              body: (
                <React.Fragment>
                  <FormattedMessage
                    {...messages.confirmationBody}
                    values={{
                      email: <strong>{email}</strong>,
                      deviceName: (
                        <EuiTextColor color='danger'>
                          <strong>
                            {selectedDeviceId ? ` device ${selectedDeviceId} ` : ` all devices `}
                          </strong>
                        </EuiTextColor>
                      ),
                    }}
                  />

                  <EuiSpacer size='m' />
                  <EuiPopover
                    id='select-mfa-device-reset-popover'
                    button={
                      <EuiButtonEmpty
                        iconType='arrowDown'
                        iconSide='right'
                        onClick={() => setIsPopoverOpen(true)}
                      >
                        <FormattedMessage
                          id='reset-mfa.select-device'
                          defaultMessage='Select MFA device to reset'
                        />
                      </EuiButtonEmpty>
                    }
                    isOpen={isPopoverOpen}
                    closePopover={() => setIsPopoverOpen(false)}
                    panelPaddingSize='none'
                  >
                    <EuiSelectable<DeviceSelectableOption>
                      id='select-mfa-device-reset-dropdown'
                      data-test-id='select-mfa-device-reset-dropdown'
                      options={options}
                      singleSelection='always'
                      onChange={(newOptions) => {
                        setOptions(newOptions)
                        setIsPopoverOpen(false)
                      }}
                      aria-label='Choose MFA device to reset'
                      listProps={{ isVirtualized: false }}
                    >
                      {(list) => <div style={{ width: 240 }}>{list}</div>}
                    </EuiSelectable>
                  </EuiPopover>
                </React.Fragment>
              ),
              confirmButtonText: formatMessage(messages.confirmAction),
            }}
          >
            {actionText}
          </DangerButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      {resetMfaMutation.error && (
        <Fragment>
          <EuiSpacer size='m' />
          <CuiAlert data-test-id='mfa-request-error' type='error'>
            {resetMfaMutation.error.errors[0]?.message || 'An error occurred while resetting MFA'}
          </CuiAlert>
        </Fragment>
      )}
    </Fragment>
  )
}

export default MfaManagement
